import type { BenLanguageBackendCode } from '@ben/common/languages';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import Input, { InputVariant } from '@ben/orion/input';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { ModularBenefitCustomModalType } from '@ben/shared/types/columbia';
import { Controller, useFormContext } from 'react-hook-form';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import Content from '../general-information/content';
import TranslatableField from '../general-information/translatable-field';
import {
  ContentContainer,
  HeaderContainer,
  TitleContainer,
} from './opt-in-out-modal.styles';

interface ModalFormProps {
  index: number;
  type: ModularBenefitCustomModalType;
  onRemove: (type: ModularBenefitCustomModalType) => void;
}

const MODAL_TYPE_LABELS: Record<ModularBenefitCustomModalType, string> = {
  [ModularBenefitCustomModalType.OPT_IN]: 'Opt-In',
  [ModularBenefitCustomModalType.OPT_OUT]: 'Opt-Out',
  [ModularBenefitCustomModalType.CHANGE]: 'Change',
};

const DISCLAIMER_HINT_TEXT: Record<ModularBenefitCustomModalType, string> = {
  [ModularBenefitCustomModalType.OPT_IN]:
    "Overwrite the 'I agree to a salary alteration and/or allowance deduction to cover my contribution' checkbox. This can be used to require users to accept additional conditions when opting in.",
  [ModularBenefitCustomModalType.OPT_OUT]:
    "Overwrite the 'I understand that I will not be able to opt in again once the enrollment window closes.' checkbox.",
  [ModularBenefitCustomModalType.CHANGE]:
    'Overwrite the default change confirmation checkbox. This can be used to require users to acknowledge salary or allowance impact when making changes.',
};

const CTA_HINT_TEXT: Record<ModularBenefitCustomModalType, string> = {
  [ModularBenefitCustomModalType.OPT_IN]:
    'Overwrite the ‘Opt into benefit’ button text. Note: this will not change the action of the button.',
  [ModularBenefitCustomModalType.OPT_OUT]:
    'Overwrite the ‘Opt out from benefit’ button text. Note: this will not change the action of the button.',
  [ModularBenefitCustomModalType.CHANGE]:
    "Overwrite the 'Save changes' button text. Note: this will not change the action of the button.",
};

export const ModalForm = ({ index, type, onRemove }: ModalFormProps) => {
  const { control } = useFormContext();

  return (
    <ContentContainer>
      <HeaderContainer>
        <TitleContainer>
          <Typography variant={TypographyVariant.h3}>
            Custom modal: {MODAL_TYPE_LABELS[type]}
          </Typography>
          <Typography variant={TypographyVariant.bodyMd1}>
            All fields below are required, unless specified optional.
          </Typography>
        </TitleContainer>
        <Button
          label="Remove"
          variant={ButtonVariant.text}
          type="button"
          onClick={() => onRemove(type)}
        />
      </HeaderContainer>
      <TranslatableField inputLabel="Modal title">
        {(language: BenLanguageBackendCode) => (
          <Controller
            control={control}
            name={`customModals.${index}.title${language}`}
            render={({ field, fieldState }) => {
              const { value, ...rest } = field;
              return (
                <Input
                  {...rest}
                  hasError={!!fieldState.error}
                  errorMessage={fieldCannotBeBlankErrorMessage}
                  value={value}
                  width="100%"
                  showLabel={false}
                  maxLength={70}
                />
              );
            }}
          />
        )}
      </TranslatableField>
      <TranslatableField inputLabel="Modal content">
        {(language: BenLanguageBackendCode) => (
          <Content
            language={language}
            errors={{}}
            controlName={`customModals.${index}.content`}
          />
        )}
      </TranslatableField>
      <TranslatableField
        inputLabel="Disclaimer checkbox"
        hintText={DISCLAIMER_HINT_TEXT[type]}
      >
        {(language: BenLanguageBackendCode) => (
          <Controller
            control={control}
            name={`customModals.${index}.disclaimer${language}`}
            render={({ field, fieldState }) => {
              const { value, ...rest } = field;
              return (
                <Input
                  {...rest}
                  hasError={!!fieldState.error}
                  errorMessage={fieldCannotBeBlankErrorMessage}
                  value={value}
                  width="100%"
                  showLabel={false}
                  variant={InputVariant.TextArea}
                  height="55px"
                />
              );
            }}
          />
        )}
      </TranslatableField>
      <TranslatableField
        inputLabel="Button label"
        hintText={CTA_HINT_TEXT[type]}
      >
        {(language: BenLanguageBackendCode) => (
          <Controller
            control={control}
            name={`customModals.${index}.ctaText${language}`}
            render={({ field, fieldState }) => {
              const { value, ...rest } = field;
              return (
                <Input
                  {...rest}
                  hasError={!!fieldState.error}
                  errorMessage={fieldCannotBeBlankErrorMessage}
                  value={value}
                  width="100%"
                  showLabel={false}
                />
              );
            }}
          />
        )}
      </TranslatableField>
    </ContentContainer>
  );
};
